import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Check } from 'lucide-react';
import { unitsLabel } from '../lib/sessionUnits';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const opts = { withCredentials: true };

const zl = (n) => `${Math.round(n).toLocaleString('pl-PL')} zł`;
const monthLabel = (key) => {
  const [y, m] = key.split('-').map(Number);
  const name = new Date(y, m - 1, 1).toLocaleDateString('pl-PL', { month: 'long' });
  return `${name} ${y}`;
};

// Client card (pay-per-training clients): trainings per month counted from
// the calendar, amount to pay and whether the month is paid. Package clients
// don't get it - their trainings stay in the packages.
export const SessionBillingPanel = ({ clientId }) => {
  const [data, setData] = useState(undefined);
  const [busy, setBusy] = useState(null);

  useEffect(() => {
    let alive = true;
    axios.get(`${API_URL}/api/billing/client/${clientId}`, opts)
      .then((r) => alive && setData(r.data)).catch(() => alive && setData(null));
    return () => { alive = false; };
  }, [clientId]);

  if (!data || !data.single) return null;

  const toggle = async (m) => {
    setBusy(m.month);
    try {
      const r = await axios.put(`${API_URL}/api/billing/client/${clientId}/${m.month}`, { paid: !m.paid }, opts);
      setData({ ...data, months: data.months.map((x) => (x.month === m.month ? { ...x, ...r.data } : x)) });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Nie udało się zapisać');
    } finally {
      setBusy(null);
    }
  };

  const unpaid = data.months.filter((m) => !m.current && !m.paid).reduce((n, m) => n + m.amount, 0);

  return (
    <section className="glass rounded-[28px] p-5 sm:p-7 flex flex-col gap-4" data-testid="session-billing-panel">
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <div>
          <p className="label-text text-[var(--text-muted)]">Rozliczenie · pojedyncze treningi</p>
          <p className="text-xs text-[var(--text-muted)] mt-1">Liczone automatycznie z kalendarza (odbyte treningi)</p>
        </div>
        {unpaid > 0 && <span className="chip border-[#C9A84C]/50 text-[#C9A84C]" data-testid="billing-unpaid">Niezapłacone: {zl(unpaid)}</span>}
      </div>
      {data.months.map((m) => (
        <div key={m.month} className="glass-soft rounded-2xl px-4 py-3 flex flex-wrap items-center justify-between gap-3" data-testid={`billing-${m.month}`}>
          <div className="min-w-0">
            <p className="text-sm font-bold capitalize">{monthLabel(m.month)}{m.current && <span className="font-normal text-[var(--text-muted)] normal-case"> · w toku</span>}</p>
            <p className="text-xs text-[var(--text-muted)]">
              {m.lines.length === 0 && !m.unpriced_trainings && unitsLabel(m.trainings)}
              {m.lines.map((l) => `${l.trainings} × ${zl(l.rate)} · ${l.label}`).join(' + ')}
              {m.unpriced_trainings > 0 && ` · ${unitsLabel(m.unpriced_trainings)} bez ceny`}
            </p>
          </div>
          <div className="flex items-center gap-3 shrink-0">
            <span className="big-num text-lg tabular-nums">{zl(m.amount)}</span>
            {m.amount > 0 && <button type="button" onClick={() => toggle(m)} disabled={busy === m.month}
              className={`chip ${m.paid ? 'text-green-500 border-green-500/40' : 'text-[var(--text-muted)]'}`}
              data-testid={`billing-paid-${m.month}`}>
              {m.paid ? <><Check className="w-3.5 h-3.5" /> Zapłacone</> : 'Niezapłacone'}
            </button>}
          </div>
        </div>
      ))}
    </section>
  );
};
